import { DEFAULT_STALE_TIME } from "@/features/utils/fetch.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QuizAPIClient } from "../api-client/quiz-api-client.ts";
import { CheckValidationIban } from "../types.ts";
import { useCompanyContext } from "@/features/company/hooks/use-company-context.ts";
import { QueryOptionsFactory } from "@/features/company/utils/index.ts";

export const useQuiz = () => {
  const { leUuid } = useCompanyContext();
  const queryClient = useQueryClient();
  const SourcesIncomeQuery = (locale: string) =>
    useQuery({
      queryKey: ["sourceIncome", locale],
      queryFn: () => QuizAPIClient.getSourcesIncome(locale),
      staleTime: DEFAULT_STALE_TIME
    });

  const MovableIncomeQuery = (locale: string) =>
    useQuery({
      queryKey: ["mouvableIncome", locale],
      queryFn: () => QuizAPIClient.getMovableIncome(),
      staleTime: DEFAULT_STALE_TIME
    });
  const ItemsAssestQuery = () =>
    useQuery({
      queryKey: ["itemAssets"],
      queryFn: () => QuizAPIClient.getCategories(),
      staleTime: DEFAULT_STALE_TIME
    });

  const OriginAssetsQuery = (locale: string) =>
    useQuery({
      queryKey: ["originAssets", locale],
      queryFn: () => QuizAPIClient.getOriginAssets(),
      staleTime: DEFAULT_STALE_TIME
    });
  const AccountReasonsQuery = (locale: string) =>
    useQuery({
      queryKey: ["accountReason", locale],
      queryFn: () => QuizAPIClient.getAccountReasons(locale),
      staleTime: DEFAULT_STALE_TIME
    });
  const PaymentsUsageQuery = (locale: string) =>
    useQuery({
      queryKey: ["paymentUsage", locale],
      queryFn: () => QuizAPIClient.getPaymentsUsage(locale),
      staleTime: DEFAULT_STALE_TIME
    });
  function useCreateQuizMutation() {
    return useMutation({
      mutationFn: QuizAPIClient.create
    });
  }

  function useCheckIbanQuizMutation() {
    return useMutation({
      mutationFn: QuizAPIClient.checkIban
    });
  }

  function useCheckValidateIbanMutation() {
    return useMutation<CheckValidationIban, Error, string>({
      mutationFn: async (ibanuuid: string) =>
        await QuizAPIClient.validateIban(ibanuuid, leUuid ?? undefined)
    });
  }

  function useRequestValidationMutation() {
    return useMutation<CheckValidationIban, Error, string>({
      mutationFn: async (iban: string) =>
        await QuizAPIClient.validateIban(iban, leUuid ?? undefined),
      onSuccess: () =>
        leUuid
          ? queryClient.invalidateQueries({
              queryKey: QueryOptionsFactory.legalEntity(leUuid ?? "").queryKey
            })
          : queryClient.invalidateQueries({ queryKey: ["user", "profile"] })
    });
  }
  function useIsValidatedIbanQuizMutation() {
    return useMutation({
      mutationFn: QuizAPIClient.isValidatedIban
    });
  }
  return {
    SourcesIncomeQuery,
    AccountReasonsQuery,
    PaymentsUsageQuery,
    useCreateQuizMutation,
    useCheckIbanQuizMutation,
    useCheckValidateIbanMutation,
    useIsValidatedIbanQuizMutation,
    MovableIncomeQuery,
    OriginAssetsQuery,
    ItemsAssestQuery,
    useRequestValidationMutation
  };
};
